// Format date to readable format
export const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

// Format time to readable format
export const formatTime = (dateString) => {
  const options = { hour: '2-digit', minute: '2-digit' };
  return new Date(dateString).toLocaleTimeString('en-US', options);
};

// Format date and time together
export const formatDateTime = (dateString) => {
  return `${formatDate(dateString)} at ${formatTime(dateString)}`;
};

// Format currency (KES)
export const formatCurrency = (amount) => {
  return `KES ${parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

// Truncate text with ellipsis
export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

// Get user's full name
export const getFullName = (user) => {
  if (!user) return '';
  return `${user.firstName} ${user.lastName}`;
};

// Calculate age from date of birth
export const calculateAge = (dateOfBirth) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

// Calculate average rating
export const calculateAverageRating = (ratings) => {
  if (!ratings || ratings.length === 0) return 0;
  const sum = ratings.reduce((total, rating) => total + rating, 0);
  return (sum / ratings.length).toFixed(1);
};

// Generate random string (for temporary IDs, etc.)
export const generateRandomString = (length = 8) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// Format phone number for display
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // If it starts with 0, convert to +254 format
  if (phoneNumber.startsWith('0')) {
    return '+254' + phoneNumber.substring(1);
  }
  
  // If it starts with 254, add + in front
  if (phoneNumber.startsWith('254')) {
    return '+' + phoneNumber;
  }
  
  return phoneNumber;
};

// Check if user has specific subscription features
export const hasSubscriptionFeature = (user, feature) => {
  if (!user || !user.subscription || !user.subscription.isActive) return false;
  
  const tierFeatures = {
    // Parent subscription features
    free: {
      childProfiles: 0,
      schoolSearch: true,
      basicReviews: true,
      progressTracking: false,
      mentorAssignment: false,
      ecommerceAccess: false,
    },
    essential: {
      childProfiles: 1,
      schoolSearch: true,
      advancedFilters: true,
      basicReviews: true,
      progressTracking: true,
      mentorAssignment: false,
      ecommerceAccess: true,
    },
    growth: {
      childProfiles: 2,
      schoolSearch: true,
      advancedFilters: true,
      detailedReviews: true,
      progressTracking: true,
      mentorAssignment: true,
      ecommerceAccess: true,
      ecommerceDiscount: 5,
    },
    plus: {
      childProfiles: "unlimited",
      schoolSearch: true,
      advancedFilters: true,
      detailedReviews: true,
      progressTracking: true,
      mentorAssignment: true,
      ecommerceAccess: true,
      ecommerceDiscount: 10,
      prioritySupport: true,
    },
    // School subscription features
    ignite: {
      teacherAccounts: 5,
      classes: 5,
      whatsappIntegration: false,
      ecommerceShop: true,
      analytics: 'basic',
      customDomain: false,
      bulkImport: false,
      discountCodesManagement: false,
    },
    elevate: {
      teacherAccounts: 'unlimited',
      classes: 'unlimited',
      whatsappIntegration: true,
      ecommerceShop: true,
      analytics: 'advanced',
      customDomain: true,
      bulkImport: true,
      discountCodesManagement: true,
    },
  };
  
  const tier = user.subscription.tier;
  return tierFeatures[tier] && tierFeatures[tier][feature];
};

// Get user role display name
export const getRoleDisplayName = (role) => {
  const roleMap = {
    system_admin: 'System Administrator',
    school_admin: 'School Administrator',
    teacher: 'Teacher',
    parent: 'Parent',
    mentor: 'Mentor',
  };
  
  return roleMap[role] || role;
};

// Check if a date is in the past
export const isDatePast = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  return date < now;
};

// Check if a date is today
export const isToday = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
};

// Get initials from name
export const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase();
};
